import classNames from "classnames";
import { CardVariant, MDOption, MetadataType } from "types";
import MetadataOptions from "../MetadataOptions";
import ChevronIcon from "assets/icons/ChevronIcon";
import { useEffect, useRef, useState } from "react";
import LabelRowWithOverflowCount from "./LabelsWithOverflow";
import { formatUnderscoreWithSpace } from "helpers/utility/formatters";

import styles from "./style.module.scss";

export interface CardMetadataLabelsProps {
  labels: string[];
  options: MDOption[];
  metadataType: MetadataType;
  cardVariant: CardVariant;
  labelIcon: JSX.Element;
  showOptionsIcon: boolean;
  selectedLabels: string[];
  onLabelSelection?: (label: string) => void;
  onViewClick: (type: MetadataType) => void;
  onViewAllClick: (type: MetadataType) => void;
}

const CardMetadataLabels = ({
  labels,
  options,
  metadataType,
  cardVariant,
  labelIcon,
  selectedLabels,
  showOptionsIcon,
  onViewClick,
  onViewAllClick,
  onLabelSelection,
}: CardMetadataLabelsProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const filterBtnRef = useRef<HTMLDivElement>(null);
  const noLabelsText = `No ${formatUnderscoreWithSpace(
    metadataType.toLowerCase()
  )} available`;

  const toggleShowOptions = () => {
    if (showOptionsIcon) {
      setShowOptions(!showOptions);
    }
  };

  const handleViewClick = (type: MetadataType) => {
    onViewClick(type);
    toggleShowOptions();
  };

  const handleViewAllClick = (type: MetadataType) => {
    onViewAllClick(type);
    toggleShowOptions();
  };

  const handleOutsideClick = (e: any) => {
    if (filterBtnRef.current && !filterBtnRef.current.contains(e.target)) {
      toggleShowOptions();
    }
  };

  useEffect(() => {
    if (showOptions) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showOptions]);

  const filterClassnames = classNames(
    styles.metadataFilter,
    styles.noDetailsClick,
    {
      [styles.objectCaptions]: metadataType === MetadataType.OBJECT_CAPTIONS,
      [styles.imageCaptions]: metadataType === MetadataType.IMAGE_CAPTIONS,
      [styles.objectLabels]: metadataType === MetadataType.OBJECT_LABELS,
      [styles.imageLabels]: metadataType === MetadataType.IMAGE_LABELS,
      [styles.userTags]: metadataType === MetadataType.USER_TAGS,
      [styles.similarityMetadataFilter]: cardVariant === CardVariant.VERTEX,
      [styles.exportMetadataFilter]: cardVariant === CardVariant.SELECTED,
      [styles.onlyIcon]: !showOptionsIcon,
    }
  );

  const noLabelsClassname = classNames(styles.noLabelsText, {
    [styles.similarityText]: cardVariant === CardVariant.VERTEX,
    [styles.exportText]: cardVariant === CardVariant.SELECTED,
  });

  return (
    <div className={styles.metadataContainer}>
      <div
        ref={filterBtnRef}
        onClick={toggleShowOptions}
        className={filterClassnames}
        data-testid="metadata-filter"
      >
        <div className={styles.labelIcon}>{labelIcon}</div>
        {showOptionsIcon && (
          <div
            className={styles.dropdownIcon}
            data-testid="md-filter-chevron-icon"
          >
            {showOptions ? (
              <ChevronIcon direction="up" dataTestId="chevron-up-icon" />
            ) : (
              <ChevronIcon direction="down" dataTestId="chevron-down-icon" />
            )}
          </div>
        )}
      </div>

      <div
        className={classNames(styles.labelsSection, {
          [styles.labelsSectionWithoutDropdown]: !showOptionsIcon,
        })}
      >
        {labels.length ? (
          <LabelRowWithOverflowCount
            labels={labels}
            cardVariant={cardVariant}
            selectedLabels={selectedLabels}
            onLabelSelection={onLabelSelection}
          />
        ) : (
          <span className={noLabelsClassname}>{noLabelsText}</span>
        )}
      </div>

      {showOptions && (
        <MetadataOptions
          options={options}
          onViewClick={handleViewClick}
          onViewAllClick={handleViewAllClick}
        />
      )}
    </div>
  );
};

export default CardMetadataLabels;
